import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { ImperativePanelHandle } from 'react-resizable-panels'

import type { PanelSizeMode } from '@verbit-ai/ai-service-web'

import { useConfig } from 'src/contexts/ConfigurationContext'
import { useFeatureFlag } from 'src/contexts/FeatureFlagContext'
import { useShouldUseInsightsFallback, useTranscriptMetadata } from 'src/contexts/Transcript/MetadataContext'
import { useIsTranscriptEmpty } from 'src/contexts/Transcript/TranscriptContext'
import { useResponsive } from 'src/hooks/useResponsive'
import { isJobLive } from 'src/models/TranscriptMetadata'
import { isChromakeyModeActive } from 'src/utils/env'
import { useLanguage } from 'src/contexts/LanguageContext'
import { useUserContext } from 'src/contexts/UserProvider'
import { AI_PANEL_ENLARGED_SIZE } from 'src/components/constants'

import { useAiPanelSizeAnimation } from './useAiPanelSizeAnimation'

export const useAiPanel = () => {
    const aiPanelRef = useRef<ImperativePanelHandle>(null)
    const panelSizeBeforeEnlargeRef = useRef<number | null>(null)
    const transcriptMetadata = useTranscriptMetadata()
    const { user, isUserFetched } = useUserContext()
    const shouldUseInsightsFallback = useShouldUseInsightsFallback()
    const { isOriginalLanguage } = useLanguage()
    const isTranscriptEmpty = useIsTranscriptEmpty()
    const [{ isFullWidthMode }] = useConfig()
    const [isAIPanelOpened, setIsAIPanelOpened] = useState(false)
    const { isAnimatingPanelSize, animateResize, cancelAnimation } = useAiPanelSizeAnimation(aiPanelRef)

    const isAiPanelAvailableByMetadata = transcriptMetadata?.generativeAiEnabled || false
    /* This feature flag should effect only live jobs  */
    const insightsPanelInitStateFlag = useFeatureFlag('insights_panel_init_open_state')
    const showInsightsPreSessionStart = useFeatureFlag('show_insights_pre_session_start')
    const isTranscriptJobLive = transcriptMetadata && isJobLive(transcriptMetadata)
    const shouldDisplayAiPanelByFlag = isTranscriptJobLive ? insightsPanelInitStateFlag : true
    const isRegisteredUserForInsights = isUserFetched && user !== null

    const shouldDisplayAiPanel =
        isAiPanelAvailableByMetadata &&
        shouldDisplayAiPanelByFlag &&
        !isChromakeyModeActive &&
        isOriginalLanguage &&
        isRegisteredUserForInsights &&
        /* if showInsightsPreSessionStart flag is true, we display the ai panel when transcript is empty */
        (showInsightsPreSessionStart || !isTranscriptEmpty || shouldUseInsightsFallback)

    const { isTablet, isMobilePhone } = useResponsive()

    const clearPanelSizeEnlargeState = useCallback(() => {
        panelSizeBeforeEnlargeRef.current = null
        cancelAnimation()
    }, [cancelAnimation])

    const expandAIPanel = useCallback((updateRef: boolean = true) => {
        setIsAIPanelOpened(true)
        if (updateRef) {
            aiPanelRef.current?.expand()
        }
    }, [])

    const collapseAIPanel = useCallback(
        (updateRef: boolean = true) => {
            clearPanelSizeEnlargeState()
            setIsAIPanelOpened(false)
            if (updateRef) {
                aiPanelRef.current?.collapse()
            }
        },
        [clearPanelSizeEnlargeState],
    )

    const enlargeAIPanel = useCallback(() => {
        const panel = aiPanelRef.current
        /* enlarging is a desktop affordance: tablet and mobile panels already open wider than the enlarged size */
        if (!panel || isTablet) {
            return
        }

        const currentSize = panel.getSize()
        if (currentSize >= AI_PANEL_ENLARGED_SIZE) {
            return
        }

        panelSizeBeforeEnlargeRef.current = currentSize
        animateResize(AI_PANEL_ENLARGED_SIZE)
    }, [animateResize, isTablet])

    const restoreAIPanelSize = useCallback(() => {
        const previousSize = panelSizeBeforeEnlargeRef.current
        /* a default event means the preview closed, so the snapshot is spent even when the panel subtree is
           detached and there is nothing left to resize - keeping it would replay a stale size on remount */
        panelSizeBeforeEnlargeRef.current = null
        if (previousSize == null || !aiPanelRef.current) {
            return
        }

        animateResize(previousSize)
    }, [animateResize])

    /* Record keeps the dispatch exhaustive: a new PanelSizeMode member fails to compile here instead of falling
       through to the restore branch. The optional call ignores values an older host cannot know about. */
    const panelSizeModeHandlers: Record<PanelSizeMode, () => void> = useMemo(
        () => ({ enlarged: enlargeAIPanel, default: restoreAIPanelSize }),
        [enlargeAIPanel, restoreAIPanelSize],
    )

    const onPanelSizeModeChange = useCallback((mode: PanelSizeMode) => panelSizeModeHandlers[mode]?.(), [panelSizeModeHandlers])

    /* AIPanel's minSize is per breakpoint, and react-resizable-panels snaps a collapsible panel shut when it is
       resized below half of that minimum, so a size captured under the desktop layout must not be replayed
       after a breakpoint change - restoring 30% under the 95% mobile minimum would collapse the panel.
       The whole enlarge episode ends here, so the transition is dropped too rather than left running across
       the relayout the new constraints trigger. */
    useEffect(() => {
        clearPanelSizeEnlargeState()
    }, [isTablet, isMobilePhone, clearPanelSizeEnlargeState])

    const onInsightsPanelAuthCheckCompleted = useCallback(
        (isAuthenticated: boolean) => {
            if (shouldDisplayAiPanel && isAuthenticated && !isTablet) {
                expandAIPanel()
            }
        },
        [isTablet, shouldDisplayAiPanel, expandAIPanel],
    )

    // when switching from default language to a translation,
    // make sure layout isn't shifted after the panel is hidden
    useEffect(() => {
        if (!shouldDisplayAiPanel) {
            collapseAIPanel()
        }
    }, [shouldDisplayAiPanel, collapseAIPanel])

    // automatically minimize Insights Panel when switching to full width mode,
    // so that it doesn't obstruct the controls in transcript toolbar
    useEffect(() => {
        if (isFullWidthMode) {
            collapseAIPanel()
        }
    }, [isFullWidthMode, collapseAIPanel])

    return {
        shouldDisplayAiPanel,
        isAIPanelOpened,
        isAnimatingPanelSize,
        onInsightsPanelAuthCheckCompleted,
        onPanelSizeModeChange,
        aiPanelRef,
        expandAIPanel,
        collapseAIPanel,
    }
}
